import type { UpdateMutationResult } from "@/customTypings/api/UpdateMutation";
import type {
    PreTreatmentCreatePayload,
    PreTreatmentUpdatePayload,
    IPreTreatmentDetail,
} from "@/customTypings/reference/PreTreatment";
import { PRE_TREATMENT_CREATE_CONFLICT } from "@/customTypings/reference/PreTreatment";
import type { ReferenceRestoreDeletedPayload } from "@/utils/reference/referenceCreateConflict";
import { authHeader, handleResponse } from "../../utils";
import { appendIncludeAuditQuery, type DetailGetByIdOptions } from "@/utils/api/detailGetOptions";
import { postReferenceCreate } from "@/utils/reference/referenceCreateConflict";

export const preTreatmentsService = {
    getById,
    create,
    restoreDeletedWithCreateValues,
    update,
    disable,
    enable,
    softDelete,
    restore,
};

function getById(id: string, options?: DetailGetByIdOptions) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        appendIncludeAuditQuery(`/api/referencedata/pre-treatments/${encodeURIComponent(id)}`, options),
        requestOptions
    ).then(handleResponse) as Promise<IPreTreatmentDetail>;
}

function create(payload: PreTreatmentCreatePayload) {
    return postReferenceCreate<IPreTreatmentDetail>(
        `/api/referencedata/pre-treatments/create`,
        payload,
        PRE_TREATMENT_CREATE_CONFLICT.deletedRestoreAvailable
    );
}

function restoreDeletedWithCreateValues(payload: ReferenceRestoreDeletedPayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/referencedata/pre-treatments/restore-deleted-with-create-values`, requestOptions).then(
        handleResponse
    ) as Promise<IPreTreatmentDetail>;
}

function update(payload: PreTreatmentUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/referencedata/pre-treatments/update`, requestOptions).then(
        handleResponse
    ) as Promise<UpdateMutationResult<IPreTreatmentDetail>>;
}

function disable(id: string) {
    return postQuery("disable", id);
}

function enable(id: string) {
    return postQuery("enable", id);
}

function softDelete(id: string) {
    return postQuery("softdelete", id);
}

function restore(id: string) {
    return postQuery("restore", id);
}

function postQuery(action: string, id: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        `/api/referencedata/pre-treatments/${action}?id=${encodeURIComponent(id)}`,
        requestOptions
    ).then(handleResponse);
}
